import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AxiosError } from "axios";
import { Tag, Tooltip } from "antd";
import {
    SyncOutlined,
    UserOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { ColumnsType } from "antd/es/table";

import { formatDateStringToDateAndTime } from "../../utils/Conversores";
import StatusTag from "./components/StatusTag";
import InvoicesService from "../../services/http/services/InvoicesService";
import useAuth from "../../hooks/useAuth";
import { EmitError } from "../../utils/EmitError";
import { EInvoiceStatus } from "../../enums";
import { EmitSuccessAlert } from "../../utils/EmitSuccessAlert";
import { IInvoice } from "../../interfaces";
import { ThemeContext } from "../../contexts/ThemeContext";
import TableActionButton from "../../components/TableActionButton";
import { useTableParams } from "../../hooks/useTableParams";
import { getSortOrder } from "../../hooks/SortOrder";
import { IActiveFilterItem } from "../../components/ActiveFiltersBar";

export interface ITableData extends IInvoice {
    rowKey: number;
    appUserId?: number;
    createdAt: Date;
    attemptDate: Date | string;
}

export type TypeSortInvoice = 'createdAt' | 'type' | 'processing_attempts' | 'attemptDate' | 'status';

export interface ICityInvoicesAwaitingFilters {
    search: string | null;
    type: string | null;
    status: string[] | string | null;
}

export default function useCityInvoices() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';
    const navigate = useNavigate();
    const { activeCity, user } = useAuth();
    const isAdmin = user?.role?.id === 1;

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isReprocessingBatch, setIsReprocessingBatch] = useState<boolean>(false);
    const [reprocessingInvoiceId, setReprocessingInvoiceId] = useState<number | null>(null);
    const [isModalConfirmReprocessOpen, setIsModalConfirmReprocessOpen] = useState<boolean>(false);
    const [rawData, setRawData] = useState<Array<ITableData>>([]);
    const [showModalInvoice, setShowModalInvoice] = useState<boolean>(false);
    const [invoiceSelected, setInvoiceSelected] = useState<ITableData | undefined>();
    const [daysWithoutAttempt, setDaysWithoutAttempt] = useState<number>(2);

    const {
        tableParams,
        handleTableChange,
        handlePaginationChange,
        handleFilterClear,
        handleClearAllFilters,
    } = useTableParams<ICityInvoicesAwaitingFilters, TypeSortInvoice>({
        storageKey: '@NotaMunicipio:invoicesAwaiting',
        defaultFilters: {
            search: null,
            type: null,
            status: null,
        },
        defaultSort: {
            field: 'createdAt',
            order: 'desc',
        },
        defaultPageSize: 50,
    });

    const handleConfirmReprocessAllInvoices = useCallback(() => {
        setIsModalConfirmReprocessOpen(true);
    }, []);

    const handleCancelReprocessAllInvoices = useCallback(() => {
        setIsModalConfirmReprocessOpen(false);
    }, []);

    const handleShowModalInvoice = useCallback(() => {
        setShowModalInvoice((prevState) => !prevState);
    }, []);

    const reprocessInvoice = useCallback(async (invoiceId: number, appUserId: number) => {
        try {
            setReprocessingInvoiceId(invoiceId);
            await InvoicesService.reprocessInvoice({
                invoiceId,
                appUserId: Number(appUserId),
                cityId: activeCity?.id!,
            });

            EmitSuccessAlert(`Nota #${invoiceId} enviada para reprocessamento!`);
            setRawData((prev) =>
                prev.map((item) =>
                    item.id === invoiceId
                        ? { ...item, status: EInvoiceStatus.reprocessing }
                        : item
                )
            );
        } catch (err) {
            if (!(err instanceof AxiosError && err.name === 'CanceledError')) {
                EmitError(err, true, 'reprocessInvoice_error');
            }
        } finally {
            setReprocessingInvoiceId(null);
        }
    }, [activeCity]);

    async function processInBatches<T>(
        items: T[],
        batchSize: number,
        callback: (item: T) => Promise<void>
    ) {
        for (let i = 0; i < items.length; i += batchSize) {
            const batch = items.slice(i, i + batchSize);
            await Promise.all(batch.map(callback));
        }
    }

    const handleReprocessAll = useCallback(async () => {
        try {
            setIsReprocessingBatch(true);

            await processInBatches(rawData, 5, async (invoice) => {
                await InvoicesService.reprocessInvoice({
                    invoiceId: invoice.id,
                    appUserId: Number(invoice.app_user_id),
                    cityId: activeCity?.id!,
                });
            });

            EmitSuccessAlert('Todas as notas foram enviadas para reprocessamento!');
            setIsModalConfirmReprocessOpen(false);

            setRawData((prev) =>
                prev.map((item) => ({ ...item, status: EInvoiceStatus.reprocessing }))
            );
        } catch (err) {
            EmitError(err, true, 'handleReprocessAll_error');
        } finally {
            setIsReprocessingBatch(false);
        }
    }, [rawData, activeCity]);

    const overdueInvoices = useMemo(() => {
        const limitDate = new Date();
        limitDate.setDate(limitDate.getDate() - (daysWithoutAttempt || 2));

        return rawData.filter((invoice) => {
            const attempt = invoice.attempt_date || invoice.attemptDate;
            if (!attempt) return true;
            return new Date(attempt) <= limitDate;
        });
    }, [rawData, daysWithoutAttempt]);

    const handleReprocessOverdue = useCallback(async () => {
        try {
            setIsReprocessingBatch(true);

            await processInBatches(overdueInvoices, 5, async (invoice) => {
                await InvoicesService.reprocessInvoice({
                    invoiceId: invoice.id,
                    appUserId: Number(invoice.app_user_id),
                    cityId: activeCity?.id!,
                });
            });

            EmitSuccessAlert(`${overdueInvoices.length} notas atrasadas enviadas para reprocessamento.`);

            const overdueIds = new Set(overdueInvoices.map((inv) => inv.id));
            setRawData((prev) =>
                prev.map((item) =>
                    overdueIds.has(item.id)
                        ? { ...item, status: EInvoiceStatus.reprocessing }
                        : item
                )
            );
        } catch (err) {
            EmitError(err, true, 'handleReprocessOverdue_error');
        } finally {
            setIsReprocessingBatch(false);
        }
    }, [overdueInvoices, activeCity]);

    const filteredAndSortedData = useMemo(() => {
        let result = [...rawData];

        if (tableParams.filters.search) {
            const query = tableParams.filters.search.toLowerCase().trim();
            result = result.filter((item) => {
                const idMatch = String(item.id).includes(query);
                const companyMatch = item.company?.toLowerCase().includes(query);
                const keyMatch = item.key ? String(item.key).toLowerCase().includes(query) : false;
                const numberMatch = item.number ? String(item.number).toLowerCase().includes(query) : false;
                return idMatch || companyMatch || keyMatch || numberMatch;
            });
        }

        if (tableParams.filters.type) {
            result = result.filter((item) => item.type === tableParams.filters.type);
        }

        if (tableParams.filters.status) {
            const statuses = Array.isArray(tableParams.filters.status)
                ? tableParams.filters.status
                : [tableParams.filters.status];
            result = result.filter((item) => statuses.includes(item.status));
        }

        const { field, order } = tableParams.sorter;
        if (field) {
            result.sort((a, b) => {
                let valA: any = (a as any)[field];
                let valB: any = (b as any)[field];

                if (field === 'createdAt' || field === 'attemptDate') {
                    valA = new Date(valA || 0).getTime();
                    valB = new Date(valB || 0).getTime();
                }

                if (typeof valA === 'string') {
                    valA = valA.toLowerCase();
                    valB = (valB || '').toLowerCase();
                }

                if (valA < valB) return order === 'asc' ? -1 : 1;
                if (valA > valB) return order === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return result;
    }, [rawData, tableParams.filters, tableParams.sorter]);

    const paginatedTableData = useMemo(() => {
        const { current, pageSize } = tableParams.pagination;
        const start = (current - 1) * pageSize;
        return filteredAndSortedData.slice(start, start + pageSize);
    }, [filteredAndSortedData, tableParams.pagination]);

    const activeFilterItems: Array<IActiveFilterItem | null> = [
        tableParams.filters.search
            ? {
                key: 'search',
                label: 'Pesquisa',
                value: tableParams.filters.search,
                onClear: () => {
                    handleFilterClear('search');
                },
            }
            : null,
        tableParams.filters.type
            ? {
                key: 'type',
                label: 'Tipo',
                value: tableParams.filters.type,
                onClear: () => handleFilterClear('type'),
            }
            : null,
        tableParams.filters.status
            ? {
                key: 'status',
                label: 'Status',
                value: Array.isArray(tableParams.filters.status)
                    ? tableParams.filters.status.join(', ')
                    : tableParams.filters.status,
                onClear: () => handleFilterClear('status'),
            }
            : null,
    ];

    const columns: ColumnsType<ITableData> = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            align: 'center' as const,
            width: 90,
            render: (id: number) => (
                <span style={{ fontWeight: 600, color: isDark ? '#93c5fd' : '#2563eb', fontSize: 13 }}>
                    #{id}
                </span>
            ),
        },
        {
            title: 'Data de Importação',
            dataIndex: 'createdAt',
            key: 'createdAt',
            sorter: true,
            sortOrder: getSortOrder('createdAt', tableParams.sorter),
            showSorterTooltip: false,
            render: (data: any) => (
                <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {data ? formatDateStringToDateAndTime(data, true) : '--'}
                </span>
            ),
            width: 190,
        },
        {
            title: 'Tipo',
            dataIndex: 'type',
            key: 'type',
            align: 'center' as const,
            sorter: true,
            sortOrder: getSortOrder('type', tableParams.sorter),
            showSorterTooltip: false,
            filters: [
                { text: 'NFS', value: 'NFS' },
                { text: 'NFC', value: 'NFC' },
                { text: 'NFE', value: 'NFE' },
            ],
            filteredValue: tableParams.filters?.type ? [tableParams.filters.type] : null,
            filterMultiple: false,
            render: (_: any, { type }: ITableData) => {
                let color = 'blue';
                if (type === 'NFC') color = 'purple';
                if (type === 'NFE') color = 'cyan';

                return (
                    <Tag
                        color={color}
                        style={{ borderRadius: 10, fontWeight: 600, fontSize: 11, margin: 0 }}
                    >
                        {type ?? '--'}
                    </Tag>
                );
            },
            width: 100,
        },
        {
            title: 'Tentativas',
            dataIndex: 'processing_attempts',
            key: 'processing_attempts',
            align: 'center' as const,
            sorter: true,
            sortOrder: getSortOrder('processing_attempts', tableParams.sorter),
            showSorterTooltip: false,
            render: (attempts: number) => {
                const count = attempts || 0;
                const color = count >= 3 ? 'error' : count === 2 ? 'warning' : 'processing';

                return (
                    <Tooltip title={`${count} de no máximo 3 tentativas automáticas`}>
                        <Tag
                            color={color}
                            style={{ borderRadius: 10, fontWeight: 600, fontSize: 12, margin: 0 }}
                        >
                            {count} de 3
                        </Tag>
                    </Tooltip>
                );
            },
            width: 130,
        },
        {
            title: 'Última Tentativa',
            dataIndex: 'attemptDate',
            key: 'attemptDate',
            sorter: true,
            sortOrder: getSortOrder('attemptDate', tableParams.sorter),
            showSorterTooltip: false,
            render: (data: any) => (
                <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 13, whiteSpace: 'nowrap' }}>
                    {data ? formatDateStringToDateAndTime(data, true) : '--'}
                </span>
            ),
            width: 190,
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            align: 'center' as const,
            sorter: true,
            sortOrder: getSortOrder('status', tableParams.sorter),
            showSorterTooltip: false,
            filters: [
                { text: 'Em processamento', value: EInvoiceStatus.processing },
                { text: 'Reprocessando', value: EInvoiceStatus.reprocessing },
            ],
            filteredValue: tableParams.filters?.status
                ? (Array.isArray(tableParams.filters.status) ? tableParams.filters.status : [tableParams.filters.status])
                : null,
            render: (_: any, { id, status, message }: ITableData) => (
                <StatusTag
                    status={status}
                    message={message}
                    key={`tag-${id}`}
                />
            ),
            width: 170,
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: 130,
            fixed: 'right' as const,
            render: (_: any, record: ITableData) => {
                const isItemReprocessing = reprocessingInvoiceId === record.id;

                return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                        <TableActionButton
                            action="view"
                            tooltip="Visualizar detalhes da nota"
                            onClick={(e) => {
                                e?.stopPropagation?.();
                                setInvoiceSelected(record);
                                handleShowModalInvoice();
                            }}
                        />
                        {record.app_user_id ? (
                            <TableActionButton
                                action="custom"
                                tooltip="Ver cadastro do usuário"
                                icon={<UserOutlined style={{ fontSize: 14, color: '#52c41a' }} />}
                                color="#52c41a"
                                bgColor={isDark ? 'rgba(82, 196, 26, 0.15)' : '#f6ffed'}
                                onClick={(e) => {
                                    e?.stopPropagation?.();
                                    navigate(`/usuarios/aplicativo/visualizar/${record.app_user_id}`);
                                }}
                            />
                        ) : null}
                        {isAdmin && record.status === EInvoiceStatus.processing ? (
                            <TableActionButton
                                action="custom"
                                tooltip={isItemReprocessing ? "Reprocessando..." : "Reprocessar esta nota"}
                                icon={<SyncOutlined spin={isItemReprocessing} style={{ fontSize: 13, color: '#fa8c16' }} />}
                                color="#fa8c16"
                                bgColor={isDark ? 'rgba(250, 140, 22, 0.15)' : '#fff7e6'}
                                onClick={(e) => {
                                    e?.stopPropagation?.();
                                    if (!isItemReprocessing) {
                                        reprocessInvoice(record.id, record.app_user_id);
                                    }
                                }}
                            />
                        ) : null}
                    </div>
                );
            },
        },
    ];

    const setTableDataFromRequest = useCallback((data: Array<IInvoice>) => {
        const newData = data.map((invoice) => ({
            ...invoice,
            rowKey: invoice.id,
            appUserId: invoice.app_user_id,
            createdAt: invoice.created_at,
            attemptDate: invoice.attempt_date ?? invoice.created_at,
        }));
        setRawData(newData);
    }, []);

    useEffect(() => {
        const controller = new AbortController();

        async function fetchInvoices() {
            try {
                setIsLoading(true);
                const response = await InvoicesService.listInvoicesAwaiting({
                    cityId: activeCity?.id!,
                    signal: controller.signal,
                });

                setTableDataFromRequest(response || []);
                setIsLoading(false);
            } catch (err) {
                if (!(err instanceof AxiosError && err.name === 'CanceledError')) {
                    EmitError(err, true, 'cityInvoicesAwaiting');
                    setIsLoading(false);
                }
            }
        }

        fetchInvoices();

        return () => controller.abort();
    }, [activeCity, setTableDataFromRequest]);

    return {
        columns,
        isLoading,
        isReprocessingBatch,
        tableData: paginatedTableData,
        totalItems: filteredAndSortedData.length,
        rawTotalCount: rawData.length,
        handleTableChange,
        handlePaginationChange,
        handleClearAllFilters,
        tableParams,
        activeFilterItems,
        handleConfirmReprocessAllInvoices,
        isModalConfirmReprocessOpen,
        handleCancelReprocessAllInvoices,
        handleReprocessAll,
        showModalInvoice,
        handleShowModalInvoice,
        invoiceSelected,
        setInvoiceSelected,
        daysWithoutAttempt,
        setDaysWithoutAttempt,
        overdueInvoices,
        handleReprocessOverdue,
        isAdmin,
    };
}
